import type React from 'react';
import * as yup from 'yup';

import { useGlobalContext } from '../../../context/global';
import { TypeButton } from '../../utils/button';
import { TypeInput } from '../../utils/input';
import { AISVG, ArrowRightSVG, BackArrowSVG } from '../../utils/svgs';
import ExtractDataAI from './extract-data-ai';
import type { IResultSummary } from './utils';
import { computeVoteBalance } from './vote-balance';
import { VoteBalanceStrip } from './VoteBalance';

export const defaultIFH: IResultSummary = {
	DataVotersOnRegister: 0,
	DataAccreditedVoters: 0,
	DataBallotPapersIssuedToPoolingUnit: 0,
	DataUnusedBallotPapers: 0,
	DataRejectedBallot: 0,
	DataTotalValidVotes: 0,
	DataTotalUsedBallotPapers: 0,
};

export const schema = {
	DataVotersOnRegister: yup.string(),
	DataAccreditedVoters: yup.string(),
	DataBallotPapersIssuedToPoolingUnit: yup.string(),
	DataUnusedBallotPapers: yup.string(),
	DataRejectedBallot: yup.string(),
	DataTotalValidVotes: yup.string(),
	DataTotalUsedBallotPapers: yup.string(),
};

const fieldGroups: { title: string; subtitle: string; fields: (keyof IResultSummary)[] }[] = [
	{
		title: 'Registration & Accreditation',
		subtitle: 'Voter registration and accreditation figures',
		fields: ['DataVotersOnRegister', 'DataAccreditedVoters'],
	},
	{
		title: 'Ballot Papers',
		subtitle: 'Ballot paper issuance and usage',
		fields: ['DataBallotPapersIssuedToPoolingUnit', 'DataUnusedBallotPapers', 'DataTotalUsedBallotPapers'],
	},
	{
		title: 'Vote Tallies',
		subtitle: 'Valid and rejected vote counts',
		fields: ['DataRejectedBallot', 'DataTotalValidVotes'],
	},
];

const fieldLabels: Record<keyof IResultSummary, string> = {
	DataVotersOnRegister: 'Voters On Register',
	DataAccreditedVoters: 'Accredited Voters',
	DataBallotPapersIssuedToPoolingUnit: 'Ballot Papers Issued',
	DataUnusedBallotPapers: 'Unused Ballot Papers',
	DataRejectedBallot: 'Rejected Ballots',
	DataTotalValidVotes: 'Valid Votes',
	DataTotalUsedBallotPapers: 'Used Ballot Papers',
};

const ResultSummaryStage = () => {
	const {
		global: {
			state: { resultSummary, isPreview: preview, onExtract, partyVotes, childCodes, resultType },
			updateState,
		},
		onStage,
		states,
	} = useGlobalContext();

	// The check reads in both directions: while the agent edits Valid Votes here,
	// they can see what the party scores already add up to.
	const balance = computeVoteBalance({ partyVotes, resultSummary, childCodes, resultType });

	const filledCount = Object.values(resultSummary).filter((v) => v > 0).length;
	const totalFields = Object.keys(resultSummary).length;

	const onCompleted = () => {
		onStage('Party Vote Count');
	};

	const onPreview = () => {
		onStage('Preview');
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { id, value } = e.target;
		updateState('resultSummary', {
			...resultSummary,
			[id]: isNaN(parseInt(value)) ? 0 : parseInt(value),
		});
	};

	return (
		<div className='f-column-33'>
			{/* AI Extraction Banner */}
			<div className='rs-ai-banner'>
				<div className='rs-ai-banner__content'>
					<div className='rs-ai-banner__icon'>
						<AISVG fill={onExtract ? 'var(--primary-700)' : ''} />
					</div>
					<div className='rs-ai-banner__text'>
						<p className='rs-ai-banner__title'>{onExtract ? 'AI Extraction Active' : 'Save time with AI'}</p>
						<p className='rs-ai-banner__subtitle'>
							{onExtract ? 'Review and correct the extracted values' : 'Auto-fill fields from your uploaded result sheet'}
						</p>
					</div>
				</div>
				<TypeButton
					title={onExtract ? 'Close' : 'Extract Data'}
					buttonSize='little'
					icon={!onExtract ? <AISVG fill='#fff' /> : undefined}
					onClick={() => updateState('onExtract', !onExtract)}
					buttonType={onExtract ? 'outlined' : 'bold'}
					disabled={states?._result?.extract_ResultUrlLoading}
					className='rs-ai-banner__btn'
				/>
			</div>

			{/* Progress indicator */}
			<div className='rs-progress'>
				<div className='rs-progress__bar'>
					<div
						className='rs-progress__fill'
						style={{ width: `${(filledCount / totalFields) * 100}%` }}
					/>
				</div>
				<span className='rs-progress__label'>
					{filledCount} of {totalFields} fields completed
				</span>
			</div>

			<div className='f-row-responsive-reverse-33'>
				<div className={`f-column-24 ${onExtract ? 'flex-basis-40' : 'w-100'}`}>
					{/* Grouped field sections */}
					{fieldGroups.map((group, gi) => (
						<div
							key={gi}
							className='rs-section'
						>
							<div className='rs-section__header'>
								<h4 className='rs-section__title'>{group.title}</h4>
								<p className='rs-section__subtitle'>{group.subtitle}</p>
							</div>
							<div className='rs-section__fields'>
								{group.fields.map((field) => (
									<TypeInput
										key={field}
										label={fieldLabels[field]}
										placeholder='0'
										id={field}
										value={resultSummary[field]}
										onClick={(e) => e.currentTarget.select()}
										onChange={handleChange}
										type='text'
										inputMode='numeric'
										pattern='[0-9]*'
									/>
								))}
							</div>
							{group.fields.includes('DataTotalValidVotes') && (
								<VoteBalanceStrip
									balance={balance}
									onReview={() => onStage('Party Vote Count')}
									reviewLabel='Party votes'
								/>
							)}
						</div>
					))}

					{/* Navigation */}
					<div className='rs-nav'>
						<button
							type='button'
							className='rs-nav__prev-btn'
							onClick={() => {
								onStage('Upload Result');
							}}
						>
							<BackArrowSVG />
							<span>Previous</span>
						</button>
						<button
							type='button'
							className='rs-nav__next-btn'
							onClick={onCompleted}
						>
							<span>Next</span>
							<ArrowRightSVG color='#fff' />
						</button>
					</div>
					{preview ? (
						<div className='f-row-12 align-items-center'>
							<TypeButton
								buttonSize='small'
								buttonType='outlined'
								title='End Preview'
								className='w-100 border-0'
								onClick={onPreview}
							/>
						</div>
					) : null}
				</div>
				<div className={`f-column-33 ${onExtract ? 'flex-basis-60' : 'd-none'}`}>
					<ExtractDataAI />
				</div>
			</div>
		</div>
	);
};

export default ResultSummaryStage;
